import { BLOCS_SALON } from "./FicheSalon.tsx";
import { FILTRES, filtrer, paginer } from "./liste.ts";
import { useEffect, useState } from "react";
import type { Entite, TypeEntite } from "@hopitech/contracts";
import { ErreurApi, creerEntite, lireEntite, listerEntites, modifierEntite, supprimerEntite } from "../lib/api.ts";
import { EDITIONS } from "../app/config.ts";
import { useApp } from "../app/state.tsx";
import { Lien, useRoute } from "../lib/router.tsx";
import { FormulaireAdmin, Panneau, type Champ } from "./ui.tsx";

/** CRUD réel des entités de gestion, piloté par une config par type. */

interface Colonne { nom: string; label: string }
export interface ConfigEntite {
  segment: string;
  type: TypeEntite;
  singulier: string;
  ajouter: string;
  finder?: boolean;
  colonnes: readonly Colonne[];
  champs: readonly Champ[];
}
const cle = (c: Champ): string => c.nom ?? c.label;

const ch = (nom: string, label: string, extra: Partial<Champ> = {}): Champ => ({ nom, label, ...extra });

export const ENTITES: Record<string, ConfigEntite> = {
  exposants: {
    segment: "exposants", type: "exposant", singulier: "exposant", ajouter: "Ajouter un exposant",
    colonnes: [{ nom: "enseigne", label: "Enseigne" }, { nom: "contact", label: "Contact" }, { nom: "email", label: "Email" }, { nom: "stand", label: "Stand" }, { nom: "emplacement", label: "Emplacement" }],
    champs: [
      ch("enseigne", "Nom de l'enseigne", { requis: true, span: 2 }),
      ch("contact", "Contact"), ch("email", "Email", { requis: true }),
      ch("telephone", "Téléphone"),
      ch("stand", "Type de stand", { type: "select", options: ["Stand nu 6 m²", "Stand nu 9 m²", "Stand nu 12 m²"] }),
      ch("placement", "Souhait de placement", { placeholder: "proche entrée…" }),
      ch("emplacement", "Emplacement attribué", { placeholder: "attribué par le bureau" }),
    ],
  },
  stagiaires: {
    segment: "stagiaires", type: "stagiaire", singulier: "stagiaire", ajouter: "Ajouter un stagiaire", finder: true,
    colonnes: [{ nom: "nom", label: "Nom" }, { nom: "prenom", label: "Prénom" }, { nom: "email", label: "Email" }, { nom: "etablissement", label: "Établissement" }, { nom: "ville", label: "Ville" }],
    champs: [
      ch("nom", "Nom", { requis: true }), ch("prenom", "Prénom", { requis: true }),
      ch("email", "Email", { requis: true }), ch("telephone", "Téléphone mobile"),
      ch("grade", "Grade"), ch("specialite", "Spécialité"), ch("service", "Service", { requis: true }),
      ch("etablissement", "Établissement", { requis: true, span: 2 }),
      ch("codePostal", "Code postal"), ch("ville", "Ville"),
      ch("formation", "Formation", { type: "select", options: ["Journée complète", "Demi-journée"] }),
    ],
  },
  intervenants: {
    segment: "intervenants", type: "intervenant", singulier: "intervenant", ajouter: "Ajouter un intervenant",
    colonnes: [{ nom: "nom", label: "Nom" }, { nom: "prenom", label: "Prénom" }, { nom: "fonction", label: "Fonction" }, { nom: "organisation", label: "Organisation" }, { nom: "email", label: "Email" }],
    champs: [
      ch("nom", "Nom", { requis: true }), ch("prenom", "Prénom", { requis: true }),
      ch("fonction", "Fonction", { requis: true }), ch("service", "Service"),
      ch("email", "Email", { requis: true }), ch("telephone", "Téléphone"),
      ch("organisation", "Organisation", { span: 2 }), ch("ville", "Ville"), ch("codePostal", "Code postal"),
    ],
  },
  conferenciers: {
    segment: "conferenciers", type: "conferencier", singulier: "conférencier", ajouter: "Ajouter un conférencier",
    colonnes: [{ nom: "nom", label: "Nom" }, { nom: "prenom", label: "Prénom" }, { nom: "sujet", label: "Sujet" }, { nom: "organisation", label: "Organisation" }],
    champs: [
      ch("nom", "Nom", { requis: true }), ch("prenom", "Prénom", { requis: true }),
      ch("sujet", "Sujet de la conférence", { span: 2 }),
      ch("email", "Email", { requis: true }), ch("organisation", "Organisation"),
    ],
  },
  organisateurs: {
    segment: "organisateurs", type: "organisateur", singulier: "organisateur", ajouter: "Ajouter un organisateur", finder: true,
    colonnes: [{ nom: "nom", label: "Nom" }, { nom: "prenom", label: "Prénom" }, { nom: "fonction", label: "Fonction" }, { nom: "etablissement", label: "Établissement" }, { nom: "ville", label: "Ville" }],
    champs: [
      ch("nom", "Nom", { requis: true }), ch("prenom", "Prénom", { requis: true }),
      ch("fonction", "Fonction", { requis: true }), ch("email", "Email", { requis: true }),
      ch("telephone", "Téléphone"),
      ch("etablissement", "Établissement", { span: 2 }), ch("ville", "Ville"), ch("codePostal", "Code postal"),
    ],
  },
  visiteurs: {
    segment: "visiteurs", type: "visiteur", singulier: "visiteur", ajouter: "Ajouter un visiteur",
    colonnes: [{ nom: "nom", label: "Nom" }, { nom: "prenom", label: "Prénom" }, { nom: "email", label: "Email" }, { nom: "etablissement", label: "Établissement" }, { nom: "tarif", label: "Tarif" }],
    champs: [
      ch("nom", "Nom", { requis: true }), ch("prenom", "Prénom", { requis: true }),
      ch("email", "Email de contact", { requis: true }), ch("telephone", "Téléphone mobile", { requis: true }),
      ch("refFacture", "Référence de facture", { span: 2 }),
      ch("etablissement", "Établissement", { requis: true }), ch("ville", "Ville", { requis: true }),
      ch("codePostal", "Code postal"),
      ch("tarif", "Tarif", { type: "select", options: ["Journée jeudi + déjeuner (30 € HT)", "Journée vendredi + déjeuner (30 € HT)", "Journée sans déjeuner (gratuit)"] }),
    ],
  },
  invites: {
    segment: "invites", type: "invite", singulier: "invité", ajouter: "Ajouter un invité",
    colonnes: [{ nom: "nom", label: "Nom" }, { nom: "prenom", label: "Prénom" }, { nom: "email", label: "Email" }, { nom: "invitePar", label: "Invité par" }, { nom: "ville", label: "Ville" }],
    champs: [
      ch("nom", "Nom", { requis: true }), ch("prenom", "Prénom", { requis: true }),
      ch("email", "Email", { requis: true }), ch("invitePar", "Invité par (exposant)"),
      ch("etablissement", "Établissement"), ch("ville", "Ville"),
    ],
  },
  ateliers: {
    segment: "ateliers", type: "atelier", singulier: "atelier", ajouter: "Ajouter un atelier",
    colonnes: [{ nom: "nom", label: "Atelier" }, { nom: "intervenant", label: "Intervenant" }, { nom: "lieu", label: "Lieu" }, { nom: "date", label: "Date" }, { nom: "heure", label: "Heure" }],
    champs: [
      ch("nom", "Nom de l'atelier", { requis: true, span: 2 }),
      ch("description", "Description", { type: "zone", span: 2 }),
      ch("lieu", "Lieu"), ch("intervenant", "Intervenant"),
      ch("date", "Date", { type: "date" }), ch("heure", "Heure de début", { placeholder: "10:00" }),
      ch("duree", "Durée (min)", { placeholder: "90" }), ch("categories", "Catégories"),
      ch("motsCles", "Mots-clés"), ch("ordre", "Ordre d'affichage"),
    ],
  },
  inscriptions: {
    segment: "inscriptions", type: "inscription", singulier: "inscription complémentaire", ajouter: "Créer une inscription",
    colonnes: [{ nom: "typeReservation", label: "Type de réservation" }, { nom: "nom", label: "Nom" }, { nom: "description", label: "Description" }],
    champs: [
      ch("typeReservation", "Type de réservation", { requis: true, span: 2, placeholder: "Forum de l'innovation…" }),
      ch("nom", "Nom", { placeholder: "identifiant (facultatif)" }),
      ch("description", "Description", { type: "zone", span: 2 }),
    ],
  },
  salons: {
    segment: "salons", type: "salon", singulier: "salon", ajouter: "Ajouter un salon",
    colonnes: [{ nom: "nom", label: "Nom" }, { nom: "annee", label: "Année" }, { nom: "ville", label: "Ville" }, { nom: "dates", label: "Dates" }, { nom: "statut", label: "Statut" }],
    champs: BLOCS_SALON.flatMap((b) => b.champs),
  },
  etablissements: {
    segment: "etablissements", type: "etablissement", singulier: "établissement", ajouter: "Ajouter un établissement",
    colonnes: [{ nom: "nom", label: "Établissement" }, { nom: "ville", label: "Ville" }, { nom: "codePostal", label: "Code postal" }, { nom: "type", label: "Type" }],
    champs: [
      ch("nom", "Nom", { requis: true, span: 2 }),
      ch("ville", "Ville", { requis: true }), ch("codePostal", "Code postal", { requis: true }),
      ch("type", "Type", { type: "select", options: ["CHU", "CH", "Clinique", "EHPAD", "Autre"] }),
    ],
  },
};

// ---------- Liste réelle ----------

export function ListeEntite({ config }: { config: ConfigEntite }): React.ReactElement {
  const app = useApp();
  const { naviguer } = useRoute();
  const [entites, setEntites] = useState<Entite[] | null>(null);
  const [erreur, setErreur] = useState<string | null>(null);

  const [recherche, setRecherche] = useState("");
  const [filtres, setFiltres] = useState<Record<string, string>>({});
  const [page, setPage] = useState(1);

  useEffect(() => {
    let actif = true;
    setEntites(null);
    setErreur(null);
    setRecherche("");
    setFiltres({});
    setPage(1);
    listerEntites(config.type, app.edition.id).then((r) => {
      if (actif) setEntites(r.entites);
    }).catch((e) => {
      if (actif) { setErreur(e instanceof ErreurApi ? e.message : "Chargement impossible."); setEntites([]); }
    });
    return () => { actif = false; };
  }, [config.type, app.edition.id]);

  const resultats = filtrer(entites ?? [], recherche, filtres);
  const pagination = paginer(resultats, page);
  const disponibles = FILTRES.map((f) => ({ ...f, valeurs: [...new Set((entites ?? []).map((e) => String(e.donnees[f.nom] ?? "")).filter(Boolean))].sort((a, b) => a.localeCompare(b, "fr")) })).filter((f) => f.valeurs.length > 0);

  function exporterCsv(): void {
    if (resultats.length === 0) return;
    const echapper = (v: unknown): string => `"${String(v ?? "").replace(/"/g, '""')}"`;
    const entete = config.colonnes.map((c) => c.label).join(";");
    const corps = resultats.map((e) => config.colonnes.map((c) => echapper((e.donnees as Record<string, unknown>)[c.nom])).join(";"));
    const blob = new Blob([[entete, ...corps].join("\n")], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${config.segment}-${app.edition.id}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
      <div className="card" style={{ display: "flex", alignItems: "center", gap: 11, flexWrap: "wrap", padding: "13px 16px" }}>
        <span className="eyebrow" style={{ color: "var(--faint)" }}>Édition</span>
        <select aria-label="Édition" className="select" style={{ width: "auto", fontWeight: 600, padding: "7px 11px" }} value={app.edition.id} onChange={(e) => void app.changerEdition(Number(e.target.value))}>
          {EDITIONS.map((e) => <option key={e.id} value={e.id}>{e.optionLabel}</option>)}
        </select>
        <label className="field" style={{ flex: "1 1 200px", margin: 0 }}>
          Recherche
          <input type="search" className="input" placeholder="Nom, e-mail, établissement…" value={recherche} onChange={(e) => { setRecherche(e.target.value); setPage(1); }} />
        </label>
        {disponibles.map((f) => (
          <label key={f.nom} className="field" style={{ flex: "1 1 160px", margin: 0 }}>
            {f.label}
            <select className="select" value={filtres[f.nom] ?? ""} onChange={(e) => { setFiltres({ ...filtres, [f.nom]: e.target.value }); setPage(1); }}>
              <option value="">Tous</option>
              {f.valeurs.map((v) => <option key={v} value={v}>{v}</option>)}
            </select>
          </label>
        ))}
        <button className="btn btn-ghost" onClick={() => { setRecherche(""); setFiltres({}); setPage(1); }}>Réinitialiser</button>
        <span style={{ flex: 1 }} />
        <button className="btn btn-ghost" onClick={exporterCsv} disabled={resultats.length === 0}>Exporter les résultats en CSV</button>
        <button className="btn btn-primary" onClick={() => naviguer(`/admin/${config.segment}/nouveau`)}>{config.ajouter}</button>
      </div>

      {erreur && <div className="pill pill-danger" style={{ alignSelf: "flex-start", textTransform: "none", fontFamily: "var(--sans)", fontSize: 12.5 }}>{erreur}</div>}
      {entites === null && <p style={{ color: "var(--muted)", fontSize: 13 }}>Chargement…</p>}
      {entites !== null && entites.length === 0 && !erreur && (
        <div className="card" style={{ padding: "48px 24px", display: "flex", flexDirection: "column", alignItems: "center", gap: 10, textAlign: "center" }}>
          <span style={{ width: 44, height: 44, borderRadius: 12, background: "var(--panel)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 19, color: "var(--faint)" }}>⊘</span>
          <h3 className="display" style={{ margin: 0, fontSize: 18 }}>Aucun {config.singulier} pour cette édition</h3>
          <button className="btn btn-primary" onClick={() => naviguer(`/admin/${config.segment}/nouveau`)}>{config.ajouter}</button>
        </div>
      )}
      {entites !== null && entites.length > 0 && (
        <div className="card" style={{ overflow: "hidden" }}>
          <div style={{ padding: "11px 16px", borderBottom: "1px solid var(--line)", background: "var(--surface-2)", fontSize: 12.5, color: "var(--muted)" }}>
            <strong style={{ color: "var(--ink)" }}>{resultats.length}</strong> résultat(s) sur {entites.length}
          </div>
          <div className="scroll-x">
            <table className="table">
              <thead><tr>{config.colonnes.map((c) => <th key={c.nom}>{c.label}</th>)}</tr></thead>
              <tbody>
                {pagination.lignes.map((e) => (
                  <tr key={e.id} style={{ cursor: "pointer" }} onClick={() => naviguer(`/admin/${config.segment}/${e.id}`)}>
                    {config.colonnes.map((c, i) => (
                      <td key={c.nom} style={i === 0 ? { fontWeight: 600, color: "var(--accent)" } : undefined}>
                        {i === 0 ? <Lien vers={`/admin/${config.segment}/${e.id}`}>{String(e.donnees[c.nom] ?? "—") || "—"}</Lien> : String(e.donnees[c.nom] ?? "—") || "—"}
                      </td>
                    ))}
                  </tr>
                ))}
                {resultats.length === 0 && <tr><td colSpan={config.colonnes.length}>Aucun résultat pour ces critères.</td></tr>}
              </tbody>
            </table>
          </div>
          <nav aria-label="Pagination" style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, padding: 16, flexWrap: "wrap" }}>
            <button className="btn btn-ghost" disabled={pagination.page === 1} onClick={() => setPage(pagination.page - 1)}>Précédent</button>
            <span role="status">Page {pagination.page} sur {pagination.pages} · 25 par page</span>
            <button className="btn btn-ghost" disabled={pagination.page === pagination.pages} onClick={() => setPage(pagination.page + 1)}>Suivant</button>
          </nav>
        </div>
      )}
    </div>
  );
}

// ---------- Formulaire réel (création / édition) ----------

export function FormEntite({ config, id }: { config: ConfigEntite; id?: string }): React.ReactElement {
  const app = useApp();
  const { naviguer } = useRoute();
  const [prefill, setPrefill] = useState<Record<string, string> | null>(id ? null : {});
  const [version, setVersion] = useState(1);
  const [erreur, setErreur] = useState<string | null>(null);

  useEffect(() => {
    if (!id) return;
    lireEntite(config.type, id)
      .then((e) => { setPrefill(versStrings(e.donnees)); setVersion(e.version); })
      .catch((e) => setErreur(e instanceof ErreurApi ? e.message : "Chargement impossible."));
  }, [config.type, id]);

  async function soumettre(v: Record<string, string>): Promise<void> {
    const donnees: Record<string, unknown> = {};
    for (const c of config.champs) donnees[cle(c)] = v[cle(c)] ?? "";
    if (id) await modifierEntite(config.type, id, donnees, version);
    else await creerEntite(config.type, app.edition.id, donnees);
    naviguer(`/admin/${config.segment}`);
  }

  const champs = config.champs.map((c) => ({ ...c, valeur: prefill?.[cle(c)] }));

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
      <Lien vers={`/admin/${config.segment}`} style={{ fontSize: 13, color: "var(--muted)" }}>← Retour à la liste</Lien>
      <h2 className="display" style={{ fontSize: 20, margin: 0 }}>{id ? `Éditer — ${config.singulier}` : `Nouvel ${config.singulier}`}</h2>
      {erreur && <div className="pill pill-danger" style={{ alignSelf: "flex-start", textTransform: "none", fontFamily: "var(--sans)", fontSize: 12.5 }}>{erreur}</div>}
      {prefill === null ? <p style={{ color: "var(--muted)" }}>Chargement…</p> : (
        <FormulaireAdmin
          key={id ?? "nouveau"}
          submit={id ? "Enregistrer les modifications" : config.ajouter}
          onSubmit={soumettre}
          blocs={[{ titre: `Informations — ${config.singulier}`, finder: config.finder, champs }]}
        />
      )}
    </div>
  );
}

// ---------- Fiche réelle ----------

export function FicheEntite({ config, id }: { config: ConfigEntite; id: string }): React.ReactElement {
  const { naviguer } = useRoute();
  const [entite, setEntite] = useState<Entite | null | undefined>(undefined);
  const [erreur, setErreur] = useState<string | null>(null);

  useEffect(() => {
    lireEntite(config.type, id).then(setEntite).catch(() => setEntite(null));
  }, [config.type, id]);

  async function supprimer(): Promise<void> {
    try { await supprimerEntite(config.type, id); naviguer(`/admin/${config.segment}`); }
    catch (e) { setErreur(e instanceof ErreurApi ? e.message : "Suppression impossible."); }
  }

  if (entite === undefined) return <p style={{ color: "var(--muted)" }}>Chargement…</p>;
  if (!entite) return <p style={{ color: "var(--muted)" }}>Entité introuvable. <Lien vers={`/admin/${config.segment}`}>← Retour</Lien></p>;
  const d = entite.donnees as Record<string, unknown>;
  const titre = String(d[config.colonnes[0]?.nom ?? "nom"] ?? config.singulier);

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16, maxWidth: 760 }}>
      <Lien vers={`/admin/${config.segment}`} style={{ fontSize: 13, color: "var(--muted)" }}>← Retour à la liste</Lien>
      <div className="card" style={{ padding: 20, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 16, flexWrap: "wrap" }}>
        <h2 className="display" style={{ fontSize: 20, margin: 0 }}>{titre}</h2>
        <div style={{ display: "flex", gap: 8 }}>
          <button className="btn btn-ghost" onClick={() => void supprimer()}>Supprimer</button>
          <button className="btn btn-primary" onClick={() => naviguer(`/admin/${config.segment}/${id}/editer`)}>Éditer</button>
        </div>
      </div>
      {erreur && <div className="pill pill-danger" style={{ alignSelf: "flex-start", textTransform: "none", fontFamily: "var(--sans)", fontSize: 12.5 }}>{erreur}</div>}
      <Panneau titre="Détails">
        <dl style={{ display: "grid", gridTemplateColumns: "200px 1fr", gap: 0, margin: 0 }}>
          {config.champs.map((c, i) => (
            <div key={cle(c)} style={{ display: "contents" }}>
              <dt style={{ padding: "10px 0", borderTop: i ? "1px solid var(--line)" : 0, fontSize: 11, textTransform: "uppercase", letterSpacing: "0.06em", color: "var(--faint-2)", fontWeight: 700 }}>{c.label}</dt>
              <dd style={{ padding: "10px 0", borderTop: i ? "1px solid var(--line)" : 0, margin: 0, fontSize: 13.5 }}>{String(d[cle(c)] ?? "") || "—"}</dd>
            </div>
          ))}
        </dl>
      </Panneau>
    </div>
  );
}

function versStrings(donnees: Readonly<Record<string, unknown>>): Record<string, string> {
  const r: Record<string, string> = {};
  for (const [k, v] of Object.entries(donnees)) r[k] = v == null ? "" : String(v);
  return r;
}
